/**
 * Enhanced notification service for prayer times.
 *
 * Improvements:
 *   - Android: POST_NOTIFICATIONS + SCHEDULE_EXACT_ALARM permissions
 *   - iOS: proper notification persistence
 *   - Better background scheduling
 *   - Notification channel setup (Android)
 *   - Graceful fallbacks
 *   - Daily reschedule on app launch
 */
import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Haptics } from '@capacitor/haptics';

const isNative = () => Capacitor.isNativePlatform?.();
const STORAGE_KEY = 'noor_prayer_alerts_scheduled';
const CHANNEL_ID = 'prayer_times';

/**
 * Create/verify Android notification channel.
 * Required on Android 8+ for notifications to appear.
 */
async function ensureNotificationChannel() {
  if (!isNative()) return;
  try {
    console.log('[Notifications] Creating channel:', CHANNEL_ID);
    if (LocalNotifications.createChannel) {
      await LocalNotifications.createChannel({
        id: CHANNEL_ID,
        name: 'Prayer Times',
        description: 'Notifications for Islamic prayer times',
        importance: 4, // high
        lightColor: '#D4AF37', // gold
        sound: 'default',
        vibration: true,
      });
      console.log('[Notifications] Channel created successfully');
    }
  } catch (e) {
    console.warn('Failed to create notification channel:', e);
  }
}

/**
 * Request Android permissions needed for notifications.
 * - POST_NOTIFICATIONS (Android 13+)
 * - SCHEDULE_EXACT_ALARM (Android 12+)
 */
async function requestAndroidNotificationPermissions() {
  if (!isNative()) return;
  try {
    // Check first — only prompt if not yet granted (avoids dialog on every launch)
    const current = await LocalNotifications.checkPermissions();
    if (current.display === 'granted') {
      console.log('[Notifications] Permission already granted, skipping request');
      return;
    }
    console.log('[Notifications] Requesting Android permissions...');
    const result = await LocalNotifications.requestPermissions();
    if (result.display !== 'granted') {
      console.warn('Notification display permission not granted:', result.display);
    } else {
      console.log('[Notifications] Display permission granted');
    }
  } catch (e) {
    console.warn('Failed to request Android notification permissions:', e);
  }
}

/* Web-only timers */
let _webTimers = [];
let _audioEl = null;

function getAudioEl() {
  if (typeof Audio === 'undefined') return null;
  if (!_audioEl) {
    _audioEl = new Audio();
    _audioEl.preload = 'none';
  }
  return _audioEl;
}

function clearWebTimers() {
  _webTimers.forEach((t) => clearTimeout(t));
  _webTimers = [];
}

export const notificationService = {
  isNative: () => !!isNative(),

  /**
   * Initialize notification system.
   * Call on app startup to set up channels and request permissions.
   */
  async initialize() {
    console.log('[Notifications] initialize() called');
    if (isNative()) {
      try {
        // Ensure notification channel exists
        console.log('[Notifications] Creating notification channel...');
        await ensureNotificationChannel();
        console.log('[Notifications] Channel created');
        
        // Request necessary permissions
        console.log('[Notifications] Requesting Android permissions...');
        await requestAndroidNotificationPermissions();
        console.log('[Notifications] Android permissions requested');
      } catch (e) {
        console.warn('Notification initialization error:', e);
      }
    }
  },

  /**
   * Request permission to display notifications.
   */
  async requestPermission() {
    console.log('=== notificationService.requestPermission() START ===');
    console.log('[Notifications] isNative:', isNative());
    console.log('[Notifications] Capacitor:', Capacitor);
    
    if (isNative()) {
      try {
        console.log('[Notifications] Calling LocalNotifications.requestPermissions()...');
        const r = await LocalNotifications.requestPermissions();
        console.log('[Notifications] requestPermissions result:', r);
        const granted = r.display === 'granted';
        console.log('[Notifications] Display permission granted:', granted);
        return granted;
      } catch (e) {
        console.error('[Notifications] requestPermissions error:', e);
        console.error('[Notifications] Error type:', typeof e, e?.constructor?.name);
        console.error('[Notifications] Error message:', e?.message);
        return false;
      }
    }

    if (typeof Notification !== 'undefined' && Notification.permission !== 'denied') {
      if (Notification.permission === 'granted') {
        console.log('[Notifications] Already granted on web');
        return true;
      }
      try {
        console.log('[Notifications] Requesting web permission...');
        const res = await Notification.requestPermission();
        console.log('[Notifications] Web permission result:', res);
        return res === 'granted';
      } catch (e) {
        console.error('[Notifications] Web permission error:', e);
        return false;
      }
    }
    console.log('[Notifications] Notifications not available');
    return false;
  },

  /**
   * Get current notification permission state.
   */
  async getPermissionState() {
    console.log('[Notifications] getPermissionState() called');
    if (isNative()) {
      try {
        console.log('[Notifications] Calling LocalNotifications.checkPermissions()...');
        const r = await LocalNotifications.checkPermissions();
        console.log('[Notifications] checkPermissions result:', r);
        return r.display; // 'granted' | 'denied' | 'prompt'
      } catch (e) {
        console.warn('[Notifications] checkPermissions error:', e);
        return 'unknown';
      }
    }
    if (typeof Notification === 'undefined') return 'unsupported';
    console.log('[Notifications] Current permission:', Notification.permission);
    return Notification.permission; // 'default' | 'granted' | 'denied'
  },

  /**
   * Trigger alert immediately (in-app or foreground).
   */
  async triggerAlert({ prayer, alertCfg, soundCatalog }) {
    if (!alertCfg || alertCfg.mode === 'off') return;

    // Haptic feedback
    if (alertCfg.mode === 'vibrate' || alertCfg.mode === 'adhan') {
      try {
        if (isNative()) {
          if (Haptics?.vibrate) {
            await Haptics.vibrate({ duration: 800 });
          }
        } else if (typeof navigator !== 'undefined' && navigator.vibrate) {
          navigator.vibrate([300, 100, 300, 100, 500]);
        }
      } catch { /* ignore */ }
    }

    // Audio (adhan mode)
    if (alertCfg.mode === 'adhan') {
      const sound = (soundCatalog || []).find((s) => s.id === alertCfg.sound_id) || soundCatalog?.[0];
      if (sound?.url && typeof Audio !== 'undefined') {
        try {
          const el = getAudioEl();
          if (el) {
            el.src = sound.url;
            el.currentTime = 0;
            await el.play();
          }
        } catch { /* autoplay may be blocked */ }
      }
    }

    // Web Notification
    if (!isNative() && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      try {
        new Notification(`${prayer.label}`, {
          body: `Il est l'heure de ${prayer.label}`,
          tag: `prayer-${prayer.key}`,
          silent: alertCfg.mode === 'silent',
          badge: '/badge-512.png',
          icon: '/icons/icon-192x192.png',
        });
      } catch { /* ignore */ }
    }
  },

  /**
   * Schedule all prayer notifications.
   * Called on page load and whenever timings/alerts change.
   */
  async scheduleAll({ prayers, alerts, soundCatalog, locationLabel }) {
    // Cancel all existing notifications
    if (isNative()) {
      try {
        console.log('[Notifications] Getting pending notifications...');
        const { notifications } = await LocalNotifications.getPending();
        if (notifications?.length) {
          console.log('[Notifications] Canceling', notifications.length, 'pending notifications...');
          await LocalNotifications.cancel({
            notifications: notifications.map((n) => ({ id: n.id })),
          });
        }
      } catch (e) {
        console.warn('[Notifications] Failed to cancel pending:', e);
      }
    }
    clearWebTimers();

    const now = Date.now();
    const notifs = [];
    let baseId = 1000;

    // Build notification list
    for (const p of prayers || []) {
      const cfg = (alerts || {})[p.key];
      if (!cfg || cfg.mode === 'off') continue;

      const atMs = p.at.getTime();
      if (atMs <= now) continue; // Skip past times

      // Main prayer notification
      const silent = cfg.mode === 'silent';
      const useAdhanSound = cfg.mode === 'adhan';
      notifs.push({
        id: baseId++,
        atMs,
        title: `${p.label} — ${locationLabel || ''}`,
        body: useAdhanSound
          ? 'Il est l\'heure de prier'
          : silent
            ? 'Heure de prière (silencieux)'
            : 'Heure de prière',
        cfg,
        p,
        isIqama: false,
      });

      // Iqama reminder (if configured)
      const delay = Number(cfg.iqama_delay_min || 0);
      if (delay > 0) {
        notifs.push({
          id: baseId++,
          atMs: atMs + delay * 60_000,
          title: `Iqama ${p.label}`,
          body: `Iqama dans ${delay} min — ${locationLabel || ''}`,
          cfg,
          p,
          isIqama: true,
        });
      }
    }

    // Schedule on native
    if (isNative()) {
      try {
        console.log('[Notifications] Scheduling', notifs.length, 'notifications...');
        const nativeNotifs = notifs.map((n) => ({
          id: n.id,
          title: n.title,
          body: n.body,
          schedule: { at: new Date(n.atMs) },
          channelId: CHANNEL_ID,
          autoCancel: true,
          // Note: custom sound requires bundling .wav/.m4a to app.
          // Fallback to OS default tone.
          sound: n.cfg.mode === 'silent' ? null : undefined,
          vibrate: n.cfg.mode === 'vibrate' || n.cfg.mode === 'adhan' ? [300, 100, 300] : [],
        }));
        console.log('[Notifications] Calling LocalNotifications.schedule()...');
        await LocalNotifications.schedule({ notifications: nativeNotifs });
        console.log('[Notifications] Native notifications scheduled successfully');
      } catch (e) {
        console.warn('LocalNotifications schedule failed:', e);
      }
    } else {
      // Web: set browser timers
      for (const n of notifs) {
        const delayMs = Math.max(0, n.atMs - Date.now());
        const t = setTimeout(async () => {
          await notificationService.triggerAlert({
            prayer: n.p,
            alertCfg: n.cfg,
            soundCatalog,
          });
        }, delayMs);
        _webTimers.push(t);
      }
    }

    // Persist schedule info
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        scheduledAt: Date.now(),
        count: notifs.length,
        nextNotificationMs: notifs[0]?.atMs,
      }));
    } catch { /* ignore */ }

    return notifs.length;
  },

  /**
   * Stop current audio playback.
   */
  async stopCurrentAudio() {
    if (_audioEl) {
      try {
        _audioEl.pause();
        _audioEl.currentTime = 0;
      } catch { /* ignore */ }
    }
  },

  /**
   * Get info about scheduled notifications (for debugging).
   */
  async getScheduledInfo() {
    try {
      const cached = localStorage.getItem(STORAGE_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  },
};
